import { useState, useMemo } from 'react';
import { Popover, Select, Loader } from '@mantine/core';
import { IconBuildingStore, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useUpdateServiceBooking } from '@/hooks/api-hooks';
import { Business, ServiceBooking, ServiceOrder } from '@turizi/shared';

interface Props {
    order: ServiceOrder;
    onCloseMenu?: () => void;
    onPopoverOpenChange?: (open: boolean) => void;
}

export default function SetOrderProviderPopoverItem({
    order,
    onCloseMenu,
    onPopoverOpenChange,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [saving, setSaving] = useState(false);
    const { mutateAsync: updateServiceBooking } = useUpdateServiceBooking();

    const handleOpenChange = (newOpened: boolean) => {
        setOpened(newOpened);
        onPopoverOpenChange?.(newOpened);
    };

    const bookings = useMemo(() => {
        const linkedBookings = order?.service_order_bookings
            ?.map(link => link?.service_booking)
            .filter(Boolean) as ServiceBooking[] || [];

        const map = new Map<number, ServiceBooking>();
        linkedBookings.forEach(b => {
            if (b?.id && !map.has(b.id)) map.set(b.id, b);
        });
        return Array.from(map.values());
    }, [order]);

    const providers = useMemo(() => {
        const map = new Map<number, Business>();
        bookings.forEach(b => {
            b.service_data?.providers?.forEach(p => {
                if (p && p.id) {
                    map.set(p.id, p);
                }
            });
        });
        return Array.from(map.values());
    }, [bookings]);

    const currentProviderId = useMemo(() => {
        if (!bookings.length) return null;
        const firstId = bookings[0]?.selected_provider_id;
        const allSame = bookings.every(b => b.selected_provider_id === firstId);
        return allSame && firstId ? firstId.toString() : (firstId ? firstId.toString() : null);
    }, [bookings]);

    const handleSelectProvider = async (val: string | null) => {
        if (!bookings.length) return;
        const newProviderId = val ? Number(val) : null;
        setSaving(true);
        try {
            await Promise.all(
                bookings.map(b =>
                    updateServiceBooking({
                        id: b.id,
                        selected_provider_id: newProviderId,
                    })
                )
            );
            handleOpenChange(false);
            onCloseMenu?.();
        } catch (error) {
            console.error('Failed to update provider for bookings:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popover
            opened={opened}
            onChange={handleOpenChange}
            position="right-start"
            withArrow
            shadow="md"
            withinPortal
        >
            <Popover.Target>
                <div
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenChange(!opened);
                    }}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer rounded-sm select-none transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <IconBuildingStore size={16} className="text-teal-600" />
                        <span>{t('provider')}</span>
                    </div>
                    <IconChevronRight size={14} className="text-gray-400" />
                </div>
            </Popover.Target>
            <Popover.Dropdown onClick={(e) => e.stopPropagation()} p="xs" className="w-[200px]">
                {providers.length === 0 ? (
                    <div className="text-xs text-gray-400 font-medium text-center py-1">
                        {t('no_providers')}
                    </div>
                ) : (
                    <Select
                        variant="filled"
                        searchable
                        clearable
                        size="xs"
                        placeholder={t('provider')}
                        value={currentProviderId}
                        data={providers.map((p) => ({
                            value: p.id.toString(),
                            label: p.name,
                        }))}
                        onChange={handleSelectProvider}
                        disabled={saving}
                        rightSection={saving ? <Loader size="xs" color="black" /> : null}
                        comboboxProps={{ keepMounted: false, withinPortal: false }}
                    />
                )}
            </Popover.Dropdown>
        </Popover>
    );
}
